import { useCallback, useEffect, useRef, useState } from "react";
import { Download, RefreshCw, Clock, CheckCircle2, XCircle, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formTypeLabel, materialStatusLabel } from "../lib/formTypes";

const REPORT_NAMES = { MATERIAL_STATUS: "Material Status", MATERIAL_EVENTS: "Material Lifecycle", DATA_ENTRY: "Data Entry", OLD_METER_RETURN: "Old Meter Return", BILLING_SUMMARY: "Billing Summary" };
const STATUS = {
  REQUESTED: { c: "bg-slate-400", i: Clock, t: "Requested" },
  PROCESSING: { c: "bg-blue-600", i: Loader2, t: "Processing" },
  READY: { c: "bg-emerald-600", i: CheckCircle2, t: "Ready" },
  FAILED: { c: "bg-red-600", i: XCircle, t: "Failed" },
  EXPIRED: { c: "bg-amber-500", i: Ban, t: "Expired" },
};
const fmt = (s) => (s || "").slice(0, 19).replace("T", " ");
const kb = (n) => (n ? `${Math.round(n / 1024)} KB` : "—");
const filterLabel = (j) => {
  const f = j.filters || {};
  if (j.report_type === "MATERIAL_STATUS") return f.current_status ? materialStatusLabel(f.current_status) : "All statuses";
  if (j.report_type === "DATA_ENTRY") return [f.status ? materialStatusLabel(f.status) : null, f.form_type ? formTypeLabel(f.form_type) : null].filter(Boolean).join(" · ") || "All statuses";
  if (j.report_type === "OLD_METER_RETURN") return f.status ? materialStatusLabel(f.status) : "All statuses";
  return "";
};

export default function DownloadCentre() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [downloading, setDownloading] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    const params = { page_size: 100 };
    if (statusFilter !== "all") params.status = statusFilter;
    try { const { data } = await api.get("/reports/exports", { params }); setRows(data.items || []); }
    catch (e) { /* silent */ }
  }, [statusFilter]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000); // lightweight status poll only
    return () => clearInterval(timer.current);
  }, [load]);

  const download = async (job) => {
    setDownloading(job.id);
    try {
      const res = await api.get(`/reports/exports/${job.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = job.file_name || "export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.status === 410 ? "Export expired — please request a new download" : (e.response?.data?.detail || "Download failed");
      toast.error(msg); load();
    } finally { setDownloading(""); }
  };

  return (
    <div>
      <PageHeader title="Download Centre" subtitle="Your requested exports (available 15 days)"
        actions={<div className="flex items-center gap-2">
          <div className="w-40"><Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger data-testid="dc-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{STATUS[s].t}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" size="sm" onClick={load} data-testid="dc-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>} />

      <div data-testid="dc-export-note" className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <span>For optimum system performance, exports are limited to 250,000 records per request. Files remain available for 15 days.</span>
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm" data-testid="download-centre">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="px-4 py-3">Report</th><th className="px-4 py-3">Project</th><th className="px-4 py-3">Requested</th><th className="px-4 py-3">By</th><th className="px-4 py-3">Rows</th><th className="px-4 py-3">Size</th><th className="px-4 py-3">Expires</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {rows.map((j) => {
              const st = STATUS[j.status] || STATUS.REQUESTED; const Icon = st.i;
              return (
                <tr key={j.id} className="border-t" data-testid={`dc-row-${j.id}`}>
                  <td className="px-4 py-3 font-medium">{REPORT_NAMES[j.report_type] || j.report_type}{filterLabel(j) && <span className="mt-0.5 block text-xs font-normal text-slate-400" data-testid={`dc-filter-${j.id}`}>{filterLabel(j)}</span>}</td>
                  <td className="px-4 py-3 text-slate-600" data-testid={`dc-project-${j.id}`}>{j.project_name || "All Projects"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(j.requested_at)}</td>
                  <td className="px-4 py-3 text-slate-500">{j.requested_by_email}</td>
                  <td className="px-4 py-3">{j.row_count ?? "—"}</td>
                  <td className="px-4 py-3">{kb(j.file_size)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(j.expires_at) || "—"}</td>
                  <td className="px-4 py-3"><Badge className={st.c}><Icon className={`mr-1 h-3 w-3 ${j.status === "PROCESSING" ? "animate-spin" : ""}`} />{st.t}</Badge>
                    {j.status === "FAILED" && j.failure_message && <div className="mt-1 text-xs text-red-500">{j.failure_message}</div>}</td>
                  <td className="px-4 py-3 text-right">
                    {j.status === "READY" && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`dc-download-${j.id}`} onClick={() => download(j)} disabled={downloading === j.id}>{downloading === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</Button>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No exports yet. Request one from Reports.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
